import React from "react";
import {
  Box,
  Divider,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ConversationAnalysisStyles } from "./ConversationAnalysisStyles";
import SessionDetailsCard from "./SessionDetailsCard";
import { ISessionDetails } from "../../models/admin/conversationAnalysis/conversationAnalysis";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";

interface IConversationSessionHistoryProps {
  sessionDetails: ISessionDetails[];
  onSelectSession: (sessionId: string) => void;
}

const ConversationSessionHistory: React.FC<
  IConversationSessionHistoryProps
> = ({ sessionDetails = [], onSelectSession }) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const { isLoading } = useAppSelector((state: RootState) => state.common);

  if (!sessionDetails) return null;
  return (
    <Box
      sx={{
        ...ConversationAnalysisStyles.sessionHistoryContainer,
        width: isMobile ? "100%" : "45vw",
        maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
      }}
      id="conversation_session_history_container"
      data-test-id="conversation_session_history_container"
      aria-label="Conversation Session History"
    >
      <Box 
        sx={ConversationAnalysisStyles.sessionsHeadingContainer}
        id="conversation_session_history_header"
        data-test-id="conversation_session_history_header"
        aria-label="Session History Header"
      >
        <Typography
          sx={{
            ...ConversationAnalysisStyles.cardHeading,
            padding: "1rem",
          }}
          id="conversation_session_history_title"
          data-test-id="conversation_session_history_title"
          aria-label="Session History"
        >
          Session History
        </Typography>
      </Box>
      <Divider />
      <Box 
        sx={ConversationAnalysisStyles.sessionHistoryCardsListContainer}
        id="conversation_session_history_list"
        data-test-id="conversation_session_history_list"
        aria-label="Session History List"
      >
        {!sessionDetails?.length && isLoading ? (
          <Box 
            sx={ConversationAnalysisStyles.sessionHistorySkeletonContainer}
            id="conversation_session_history_loading"
            data-test-id="conversation_session_history_loading"
            aria-label="Loading Session History"
          >
            {Array.from(Array(4)).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="100%"
                height={100}
                animation="wave"
                id={`conversation_session_history_skeleton_${index}`}
                data-test-id={`conversation_session_history_skeleton_${index}`}
                aria-label="Loading Session"
              />
            ))}
          </Box>
        ) : sessionDetails?.length ? (
          sessionDetails.map((session, index) => (
            <SessionDetailsCard
              key={index}
              session={session}
              onSelectSession={onSelectSession}
            />
          ))
        ) : (
          <Typography 
            textAlign="center" 
            fontWeight="bold"
            id="conversation_session_history_empty"
            data-test-id="conversation_session_history_empty"
            aria-label="No Session History Available"
          >
            No Session History available.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ConversationSessionHistory;
